/**
 * @fileoverview Hook for the per-department Parcel Data tab in the system-web
 * DepartmentPanel slide-in. Loads only the data relevant to a single
 * department: the matched Regrid cache file (by FIPS) and that department's
 * automated import history.
 * @module system-web/hooks/useDepartmentParcelData
 *
 * Responsibilities:
 * - Subscribe to the `geo_cache_files` Dexie table for the single cache file
 *   matching the department's Regrid FIPS code (from `tenant.parcelImport.regridFileId`
 *   or `tenant.jurisdiction.fipsCode`).
 * - Subscribe to the `import_runs` Dexie table filtered by `tenantId` for the
 *   department's automated import history.
 * - Trigger an online refresh of cache files on mount so the matched file
 *   metadata is current (separate effect from the liveQuery read, per the
 *   liveQuery contract).
 * - Expose the same write-through mutations used by the admin section
 *   (saveTokenEntitlement, adminBypassUpdate) so the per-department tab can
 *   edit tokens and trigger admin bypass updates inline.
 *
 * Dependencies:
 * - parcelDataAdminService (reads + mutations).
 * - dexie/liveQuery.
 * - @firesquirrel/local-db (getSystemDexieRaw for raw table reads).
 *
 * Architecture:
 * - liveQuery observed functions are pure Dexie reads — no writes, no
 *   wakeLocalWrite(), no API calls (per the liveQuery contract rule).
 * - refreshCacheFiles/saveTokenEntitlement/adminBypassUpdate are imperative —
 *   called from UI handlers or the mount effect, NEVER inside liveQuery.
 *
 * Notes:
 * - The tenant's token entitlement is read directly from the `tenant` prop
 *   passed into the hook (the parent DepartmentPanel already has the tenant
 *   record from its own liveQuery). This hook does NOT re-read the tenant
 *   record — it only reads the cache file and import history.
 * - The FIPS resolution order matches ParcelImportSection.tsx:
 *   `tenant.parcelImport.regridFileId` → `tenant.jurisdiction.fipsCode` →
 *   `tenant.jurisdiction.county.code` (when it is a 5-digit FIPS).
 *
 * @localFirst
 * Source of Truth: Dexie cache for reads; system-api for cache file metadata
 *   refresh + entitlement writes. Tenant token entitlements are
 *   server-authoritative (Phase 8 routes persist them).
 * Write Flow: service -> apiFetch (PUT/POST) -> Dexie put; liveQuery re-emits.
 * Sync Strategy: on-demand fetch for cache files; standard pull for import_runs;
 *   write-through for entitlement edits (API then optimistic Dexie put).
 * Conflict Resolution: server-authoritative for entitlements; the pull pipeline
 *   reconciles any divergence on the next sync cycle.
 */

import { useState, useEffect, useCallback } from 'react';
import { liveQuery } from 'dexie';
import { getSystemDexieRaw, getSystemDb } from '@firesquirrel/local-db';
import type { BackgroundJob } from '@firesquirrel/local-db';
import {
  listImportHistoryForTenant,
  getLastOwnerUpdateRunDate,
  refreshCacheFiles as serviceRefreshCacheFiles,
  setTenantTokenEntitlement as serviceSetTenantTokenEntitlement,
  adminBypassUpdate as serviceAdminBypassUpdate,
  saveParcelConfig as serviceSaveParcelConfig,
  triggerRegridImport as serviceTriggerRegridImport,
  cancelRegridImport as serviceCancelRegridImport,
  estimateParcels as serviceEstimateParcels,
  type GeoCacheFile,
  type ImportHistoryEntry,
  type ParcelImportConfig,
  type ParcelEstimateResult,
} from '../services/parcelDataAdminService';

/**
 * Resolve the 5-digit county FIPS code for a tenant record.
 * Order: `parcelImport.regridFileId` → `jurisdiction.fipsCode` →
 * `jurisdiction.county.code` (when it is a 5-digit string).
 */
function resolveFips(tenant: any): string | null {
  const parcelImport = tenant?.parcelImport;
  if (typeof parcelImport?.regridFileId === 'string' && /^\d{5}$/.test(parcelImport.regridFileId)) {
    return parcelImport.regridFileId;
  }
  const jurisdiction = tenant?.jurisdiction ?? {};
  if (typeof jurisdiction.fipsCode === 'string' && /^\d{5}$/.test(jurisdiction.fipsCode)) {
    return jurisdiction.fipsCode;
  }
  const countyCode = jurisdiction?.county?.code;
  if (typeof countyCode === 'string' && /^\d{5}$/.test(countyCode)) {
    return countyCode;
  }
  return null;
}

/**
 * @hook useDepartmentParcelData
 *
 * Purpose:
 * - Drive the per-department Parcel Data tab in the DepartmentPanel from
 *   local-first Dexie state.
 *
 * Props:
 * - tenant: The current department record (must contain `_id`; optionally
 *   `parcelImport.regridFileId`, `jurisdiction.fipsCode`, `updateTokens`).
 *
 * Returns:
 * - fips: The resolved 5-digit county FIPS code for the department (or null).
 * - cacheFile: The matched Regrid master-cache file from Dexie (or null).
 * - importHistory: The department's automated import runs from Dexie.
 * - lastOwnerCsvUpdate: The newest completed parcelOwnerUpdate run's
 *   dataAsOf/completedAt (the 'department-csv' owner-data candidate for
 *   the shared panel, Phase 5c).
 * - isLoading: True during the initial liveQuery setup.
 * - isRefreshing: True while an online cache file refresh is in flight.
 * - error: Non-null when any operation failed.
 * - refreshCacheFiles: Re-fetch cache file metadata from system-api.
 * - saveTokenEntitlement: Write-through a tenant's token entitlement.
 * - adminBypassUpdate: Record an admin parcel data update WITHOUT consuming a token.
 * - clearError: Reset the error state.
 *
 * Side Effects:
 * - On mount: subscribes to two Dexie liveQuery subscriptions AND triggers
 *   an online cache file refresh (in separate effects).
 *
 * Offline Behavior:
 * - cacheFile and importHistory from Dexie are available offline.
 * - refreshCacheFiles requires connectivity.
 * - saveTokenEntitlement and adminBypassUpdate require connectivity.
 *
 * @errorHandling
 * Strategy:   All async errors are caught and stored in `error` state.
 * User Impact: The Parcel Data tab shows an inline error banner.
 * Logging:    Delegated to parcelDataAdminService (trackEvent/trackException).
 */
export function useDepartmentParcelData(tenant: any) {
  const tenantId: string = tenant?._id ?? '';
  const fips = resolveFips(tenant);

  const [cacheFile, setCacheFile] = useState<GeoCacheFile | null>(null);
  const [importHistory, setImportHistory] = useState<ImportHistoryEntry[]>([]);
  const [lastOwnerCsvUpdate, setLastOwnerCsvUpdate] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // -------------------------------------------------------------------------
  // liveQuery — matched cache file (pure read, no writes)
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (!fips) {
      setCacheFile(null);
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    let sub: { unsubscribe(): void } | null = null;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;

    const attach = () => {
      const raw = getSystemDexieRaw();
      if (!raw) {
        retryTimer = setTimeout(attach, 500);
        return;
      }
      sub = liveQuery(async () => {
        const doc = (await raw.table('geo_cache_files').get(fips)) as GeoCacheFile | undefined;
        return doc ?? null;
      }).subscribe({
        next: (row) => {
          if (cancelled) return;
          setCacheFile(row);
          setIsLoading(false);
        },
        error: (err) => {
          if (cancelled) return;
          console.error('[useDepartmentParcelData] cacheFile liveQuery error:', err);
          setError(err instanceof Error ? err.message : 'Failed to load cache file');
          setIsLoading(false);
        },
      });
    };

    attach();

    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
      sub?.unsubscribe();
    };
  }, [fips]);

  // -------------------------------------------------------------------------
  // liveQuery — per-tenant import history (pure read, no writes)
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (!tenantId) {
      setImportHistory([]);
      return;
    }
    let cancelled = false;
    let sub: { unsubscribe(): void } | null = null;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;

    const attach = () => {
      const raw = getSystemDexieRaw();
      if (!raw) {
        retryTimer = setTimeout(attach, 500);
        return;
      }
      sub = liveQuery(() => listImportHistoryForTenant(tenantId)).subscribe({
        next: (entries) => {
          if (cancelled) return;
          setImportHistory(entries);
        },
        error: (err) => {
          if (cancelled) return;
          console.error('[useDepartmentParcelData] importHistory liveQuery error:', err);
          setError((prev) => prev ?? (err instanceof Error ? err.message : 'Failed to load import history'));
        },
      });
    };

    attach();

    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
      sub?.unsubscribe();
    };
  }, [tenantId]);

  // -------------------------------------------------------------------------
  // liveQuery — last parcelOwnerUpdate run (pure read, no writes)
  // Phase 5c (plan §C.5): feeds the shared panel's last-owner-data-update
  // surface as the 'department-csv' candidate. Field-edit stamps live on
  // tenant-DB entities which system-web does not sync, so only the
  // regrid/department-csv candidates are available here.
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (!tenantId) {
      setLastOwnerCsvUpdate(null);
      return;
    }
    let cancelled = false;
    let sub: { unsubscribe(): void } | null = null;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;

    const attach = () => {
      const raw = getSystemDexieRaw();
      if (!raw) {
        retryTimer = setTimeout(attach, 500);
        return;
      }
      sub = liveQuery(() => getLastOwnerUpdateRunDate(tenantId)).subscribe({
        next: (date) => {
          if (cancelled) return;
          setLastOwnerCsvUpdate(date);
        },
        error: () => {
          // Supplementary surface — never fail the panel over it.
        },
      });
    };

    attach();

    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
      sub?.unsubscribe();
    };
  }, [tenantId]);

  // -------------------------------------------------------------------------
  // Open the system DB on mount so getSystemDexieRaw() is available.
  // -------------------------------------------------------------------------
  useEffect(() => {
    void getSystemDb().catch((err) => {
      console.error('[useDepartmentParcelData] Failed to open system DB on mount:', err);
    });
  }, []);

  // -------------------------------------------------------------------------
  // refreshCacheFiles — imperative online refresh (NOT inside liveQuery)
  // -------------------------------------------------------------------------
  const refreshCacheFiles = useCallback(async () => {
    setIsRefreshing(true);
    setError(null);
    try {
      await serviceRefreshCacheFiles();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to refresh cache files');
    } finally {
      setIsRefreshing(false);
    }
  }, []);

  // On mount: refresh cache files from the server so the matched file is current.
  useEffect(() => {
    void refreshCacheFiles();
  }, [refreshCacheFiles]);

  // -------------------------------------------------------------------------
  // saveTokenEntitlement — write-through API + Dexie put (Phase 8)
  // -------------------------------------------------------------------------
  const saveTokenEntitlement = useCallback(async (id: string, tokensPerYear: number): Promise<boolean> => {
    setError(null);
    try {
      await serviceSetTenantTokenEntitlement(id, tokensPerYear);
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save token entitlement');
      return false;
    }
  }, []);

  // -------------------------------------------------------------------------
  // adminBypassUpdate — write-through API + Dexie put (Phase 8)
  // -------------------------------------------------------------------------
  const adminBypassUpdate = useCallback(async (id: string, newVersion: string): Promise<boolean> => {
    setError(null);
    try {
      await serviceAdminBypassUpdate(id, newVersion);
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to record admin bypass update');
      return false;
    }
  }, []);

  // -------------------------------------------------------------------------
  // estimateParcels — online-only call that downloads the GeoParquet blob and
  // runs a DuckDB COUNT with ST_Intersects against the department boundary.
  // Returns { matchedCount, sourceCount }. Does NOT write to Dexie — the
  // result is transient and displayed inline in the UI.
  // -------------------------------------------------------------------------
  const [isEstimating, setIsEstimating] = useState(false);
  const [parcelEstimate, setParcelEstimate] = useState<ParcelEstimateResult | null>(null);

  const estimateParcels = useCallback(async (id: string): Promise<ParcelEstimateResult | null> => {
    setError(null);
    setIsEstimating(true);
    try {
      const result = await serviceEstimateParcels(id);
      setParcelEstimate(result);
      return result;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to estimate parcels');
      return null;
    } finally {
      setIsEstimating(false);
    }
  }, []);

  // Keep this hook before newly-added hooks so hot reload preserves the
  // existing hook sequence for mounted department panels.
  const clearError = useCallback(() => setError(null), []);

  // -------------------------------------------------------------------------
  // saveParcelConfig — write-through API + Dexie put (Phase 9+)
  // Saves parcelImport + updateTokens on the tenant document from the admin
  // DepartmentParcelDataTab config form.
  // -------------------------------------------------------------------------
  const saveParcelConfig = useCallback(async (id: string, config: ParcelImportConfig): Promise<boolean> => {
    setError(null);
    try {
      await serviceSaveParcelConfig(id, config);
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save parcel configuration');
      return false;
    }
  }, []);

  // -------------------------------------------------------------------------
  // triggerImport — enqueues a Regrid ingestion job (admin bypass, no token
  // consumed). Returns the jobId for progress tracking. The active import job
  // is tracked via the importJob state + liveQuery subscription below.
  // -------------------------------------------------------------------------
  const [activeImportJobId, setActiveImportJobId] = useState<string | null>(null);

  const triggerImport = useCallback(async (
    id: string,
    overrides?: {
      filterMode?: 'all' | 'department' | 'fireHazard';
      hazardLevels?: string[];
      createOwnerContacts?: boolean;
    },
  ): Promise<string | null> => {
    setError(null);
    try {
      const result = await serviceTriggerRegridImport(id, overrides);
      setActiveImportJobId(result.jobId);
      return result.jobId;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to trigger parcel import');
      return null;
    }
  }, []);

  // -------------------------------------------------------------------------
  // cancelImport — cancels the active regridIngestion job for this tenant.
  // Calls the server-side cancel route, which marks the backgroundJobs doc
  // as cancelled. The optimistic Dexie update in the service makes the
  // spinner disappear immediately.
  // -------------------------------------------------------------------------
  const cancelImport = useCallback(async (id: string, jobId?: string): Promise<boolean> => {
    setError(null);
    try {
      await serviceCancelRegridImport(id, jobId);
      // Clear the active job ID so the UI transitions away from the
      // progress display. The liveQuery will also re-emit when the Dexie
      // row is optimistically updated by the service.
      setActiveImportJobId(null);
      setImportJob(null);
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to cancel parcel import');
      return false;
    }
  }, []);

  // -------------------------------------------------------------------------
  // liveQuery — active regridIngestion background job for this tenant
  // (pure read, no writes). Subscribes to the background_jobs Dexie table
  // filtered by tenantId + jobType='regridIngestion' + status in
  // (pending, running). This provides live progress (parcels processed /
  // total) as the worker updates the backgroundJobs document.
  // -------------------------------------------------------------------------
  const [importJob, setImportJob] = useState<BackgroundJob | null>(null);

  useEffect(() => {
    if (!tenantId) {
      setImportJob(null);
      return;
    }
    let cancelled = false;
    let sub: { unsubscribe(): void } | null = null;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;

    const attach = () => {
      const raw = getSystemDexieRaw();
      if (!raw) {
        retryTimer = setTimeout(attach, 500);
        return;
      }
      sub = liveQuery(async () => {
        // Find the most recent regridIngestion job for this tenant that is
        // either pending or running. When a job completes/fails, it remains
        // in the table but with a terminal status — the UI uses `importJob`
        // to show live progress, then transitions to the import history
        // table once the job is terminal.
        const jobs = await raw
          .table('background_jobs')
          .where('jobType')
          .equals('regridIngestion')
          .filter((j: any) =>
            j?.tenantId === tenantId &&
            (j?.status === 'pending' || j?.status === 'running'),
          )
          .toArray() as BackgroundJob[];
        // Sort by startedAt descending — most recent first
        jobs.sort((a, b) => (b.startedAt ?? '').localeCompare(a.startedAt ?? ''));
        return jobs[0] ?? null;
      }).subscribe({
        next: (job) => {
          if (cancelled) return;
          setImportJob(job);
          if (job) {
            setActiveImportJobId(job._id);
          } else {
            // No active job — clear the active job ID
            setActiveImportJobId(null);
          }
        },
        error: (err) => {
          if (cancelled) return;
          console.error('[useDepartmentParcelData] importJob liveQuery error:', err);
        },
      });
    };

    attach();

    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
      sub?.unsubscribe();
    };
  }, [tenantId]);

  // -------------------------------------------------------------------------
  // Also watch a specific job by ID when triggered — this catches the job
  // even before the tenantId-filtered liveQuery picks it up (the job may
  // arrive in Dexie slightly after the trigger response).
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (!activeImportJobId) return;
    let cancelled = false;
    let sub: { unsubscribe(): void } | null = null;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;

    const attach = () => {
      const raw = getSystemDexieRaw();
      if (!raw) {
        retryTimer = setTimeout(attach, 500);
        return;
      }
      sub = liveQuery(async () => {
        const doc = await raw.table('background_jobs').get(activeImportJobId) as BackgroundJob | undefined;
        return doc ?? null;
      }).subscribe({
        next: (job) => {
          if (cancelled) return;
          if (job) {
            setImportJob(job);
            // Only auto-clear COMPLETED jobs after a short delay so the UI
            // transitions to the history view. FAILED and CANCELLED jobs stay
            // visible until the user dismisses them or triggers a new import —
            // otherwise the error vanishes before the user can read it.
            if (job.status === 'completed') {
              setTimeout(() => {
                if (!cancelled) setActiveImportJobId(null);
              }, 3000);
            }
          }
        },
        error: (err) => {
          if (cancelled) return;
          console.error('[useDepartmentParcelData] activeJob liveQuery error:', err);
        },
      });
    };

    attach();

    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
      sub?.unsubscribe();
    };
  }, [activeImportJobId]);

  // -------------------------------------------------------------------------
  // dismissImportJob — clear the import job display so the progress card
  // disappears. Used by the ImportProgressDisplay dismiss button for
  // terminal-state jobs (failed/cancelled/completed) that the user has read.
  // -------------------------------------------------------------------------
  const dismissImportJob = useCallback(() => {
    setActiveImportJobId(null);
    setImportJob(null);
  }, []);

  return {
    fips,
    cacheFile,
    importHistory,
    lastOwnerCsvUpdate,
    isLoading,
    isRefreshing,
    error,
    refreshCacheFiles,
    saveTokenEntitlement,
    adminBypassUpdate,
    saveParcelConfig,
    triggerImport,
    cancelImport,
    importJob,
    activeImportJobId,
    dismissImportJob,
    clearError,
    estimateParcels,
    isEstimating,
    parcelEstimate,
  };
}
